import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { ImageOff } from "lucide-react";

/**
 * Admin thumbnail that fetches its image_url from /admin/photos/{id}.
 * - Default (lazy=true): waits for the card to enter the browser viewport (IntersectionObserver).
 * - Set lazy={false} when the thumbnail lives inside a modal / scroll container where
 *   IntersectionObserver's default root (viewport) can't detect scrolling — the image
 *   loads immediately on mount instead.
 */
const AdminPhotoThumb = ({ photoId, alt = "", className = "", onClick, testid, lazy = true }) => {
  const ref = useRef(null);
  const [src, setSrc] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | loaded | error

  useEffect(() => {
    let cancelled = false;
    const fetchNow = async () => {
      setState("loading");
      try {
        const r = await api.get(`/admin/photos/${photoId}`);
        if (cancelled) return;
        setSrc(r.data.image_url);
        setState("loaded");
      } catch {
        if (!cancelled) setState("error");
      }
    };
    if (!lazy) {
      fetchNow();
      return () => { cancelled = true; };
    }
    const node = ref.current;
    if (!node) return () => { cancelled = true; };
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        fetchNow();
      },
      { rootMargin: "200px" }
    );
    io.observe(node);
    return () => { cancelled = true; io.disconnect(); };
  }, [photoId, lazy]);

  return (
    <div
      ref={ref}
      className={`w-full h-full bg-[#EBF4F6] flex items-center justify-center overflow-hidden ${className}`}
      onClick={() => src && onClick?.(src)}
      data-testid={testid}
    >
      {state === "loaded" && src ? (
        <img src={src} alt={alt} className="w-full h-full object-cover cursor-zoom-in hover:scale-105 transition-transform" />
      ) : state === "error" ? (
        <div className="flex flex-col items-center gap-1 text-red-500">
          <ImageOff size={22} />
          <span className="text-[10px] font-bold">تعذّر التحميل</span>
        </div>
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#EBF4F6] to-[#7AB2B2]/40 animate-pulse" />
      )}
    </div>
  );
};

export default AdminPhotoThumb;
